/**
 * KioskDevices (ADR-032 Phase 4) — register + manage shared in-store Kiosk
 * devices (iPad/Android tablet) that workers walk up to and punch via face
 * 1:N. Admin registers a device bound to a geofence → gets a one-time pairing
 * code to type into the device's QuTime app → device pairs → active.
 *
 * Backend: /v1/attendance/kiosk/devices (POST/GET/PATCH/DELETE) + /settings
 * (GET/PATCH), permission attendance.kiosk.manage. Device-facing punch endpoints
 * (/v1/kiosk/*) live on the device, not here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/VpnKey';
import RefreshIcon from '@mui/icons-material/Refresh';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

import { ApiError, attendanceApi } from '../../../api';
import type {
  KioskDevice,
  KioskSettings,
  Geofence,
} from '../../../api/attendance';
import { ConfirmDialog } from '../../../components/primitives';
import { activeLocale } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';

const KioskDevices: React.FC = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [settings, setSettings] = useState<KioskSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Register dialog
  const [openRegister, setOpenRegister] = useState(false);
  const [regName, setRegName] = useState('');
  const [regGeofence, setRegGeofence] = useState('');
  const [registering, setRegistering] = useState(false);

  // Pairing-code dialog (shown once after register)
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpires, setPairingExpires] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke
  const [toRevoke, setToRevoke] = useState<KioskDevice | null>(null);
  // Hard-delete (remove revoked/never-paired devices off the list)
  const [toDelete, setToDelete] = useState<KioskDevice | null>(null);

  // Settings local edit
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingDeviceIds, setSavingDeviceIds] = useState<Set<string>>(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [devs, geos, st] = await Promise.all([
        attendanceApi.kioskApi.listDevices(),
        attendanceApi.geofencesApi.list(),
        attendanceApi.kioskApi.getSettings(),
      ]);
      setDevices(devs);
      setGeofences(geos);
      setSettings(st);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const stats = useMemo(() => {
    const total = devices.length;
    const active = devices.filter((d) => d.status === 'active').length;
    const pending = devices.filter((d) => d.status === 'pending_pair').length;
    return { total, active, pending };
  }, [devices]);

  const handleRegister = async () => {
    if (!regName.trim() || !regGeofence) return;
    setRegistering(true);
    try {
      const res = await attendanceApi.kioskApi.registerDevice({
        name: regName.trim(),
        geofence_id: regGeofence,
        enforce_device_location: true,
      });
      setOpenRegister(false);
      setRegName('');
      setRegGeofence('');
      setPairingCode(res.pairing_code);
      setPairingExpires(res.pairing_expires_at);
      setCopied(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.registerFailed'));
    } finally {
      setRegistering(false);
    }
  };

  const handleRevoke = async () => {
    if (!toRevoke) return;
    try {
      await attendanceApi.kioskApi.revokeDevice(toRevoke.id);
      setToast(t('attendance:kiosk.toast.revoked', { name: toRevoke.name }));
      setToRevoke(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.revokeFailed'));
      throw e;
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await attendanceApi.kioskApi.deleteDevice(toDelete.id);
      setToast(t('attendance:kiosk.toast.deleted', { name: toDelete.name }));
      setToDelete(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.deleteFailed'));
      throw e;
    }
  };

  const handleRegenerate = async (device: KioskDevice) => {
    try {
      const res = await attendanceApi.kioskApi.regeneratePairingCode(device.id);
      setPairingCode(res.pairing_code);
      setPairingExpires(res.pairing_expires_at);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.regenerateFailed'));
    }
  };

  const [reindexing, setReindexing] = useState(false);
  const handleReindex = async () => {
    setReindexing(true);
    try {
      const res = await attendanceApi.kioskApi.reindexFaces();
      setToast(t('attendance:kiosk.toast.reindexed', { indexed: res.indexed }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.reindexFailed'));
    } finally {
      setReindexing(false);
    }
  };

  const handleCopyCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
    } catch {
      /* clipboard blocked — the code is visible to type manually */
    }
  };

  const patchSetting = async (patch: Partial<KioskSettings>) => {
    if (!settings) return;
    const prev = settings;
    setSettings({ ...settings, ...patch });
    setSavingSettings(true);
    try {
      const next = await attendanceApi.kioskApi.updateSettings(patch);
      setSettings(next);
      setToast(t('attendance:kiosk.toast.settingsSaved'));
    } catch (e) {
      setSettings(prev); // revert optimistic update
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.settingsFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  const patchDeviceLocation = async (device: KioskDevice, enabled: boolean) => {
    setSavingDeviceIds((current) => new Set(current).add(device.id));
    setDevices((current) => current.map((item) => (
      item.id === device.id ? { ...item, enforce_device_location: enabled } : item
    )));
    try {
      const updated = await attendanceApi.kioskApi.updateDevice(device.id, {
        enforce_device_location: enabled,
      });
      setDevices((current) => current.map((item) => (item.id === device.id ? updated : item)));
      setToast(t('attendance:kiosk.toast.locationSaved', { name: device.name }));
    } catch (e) {
      setDevices((current) => current.map((item) => (item.id === device.id ? device : item)));
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:kiosk.toast.locationFailed'));
    } finally {
      setSavingDeviceIds((current) => {
        const next = new Set(current);
        next.delete(device.id);
        return next;
      });
    }
  };

  const geoName = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of geofences) m.set(g.id, g.name);
    return m;
  }, [geofences]);

  const statusChip = (status: string) => {
    if (status === 'active') {
      return <Chip size="small" color="success" label={t('attendance:kiosk.status.active')} sx={{ borderRadius: 1, height: 22 }} />;
    }
    if (status === 'pending_pair') {
      return <Chip size="small" color="warning" variant="outlined" label={t('attendance:kiosk.status.pending')} sx={{ borderRadius: 1, height: 22 }} />;
    }
    return <Chip size="small" variant="outlined" label={t('attendance:kiosk.status.revoked')} sx={{ borderRadius: 1, height: 22 }} />;
  };

  if (loading && devices.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && devices.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchAll}>{t('common:actions.retry')}</Button>}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Alert severity="info" sx={{ width: '100%', mb: 2 }} icon={<TabletMacIcon />}>
        <AlertTitle>{t('attendance:kiosk.banner.title')}</AlertTitle>
        <Typography component="span" variant="body2">{t('attendance:kiosk.banner.body')}</Typography>{' '}
        <Link
          href="https://svdy.com/solutions/kiosk/"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontWeight: 600 }}
        >
          {t('attendance:kiosk.banner.learnMore')}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      </Alert>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('attendance:kiosk.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:kiosk.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} sx={{ mr: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpenRegister(true)}
            disabled={geofences.length === 0}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:kiosk.registerButton')}
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>{stats.total}</strong> {t('attendance:kiosk.stats.total')} ·{' '}
        <strong>{stats.active}</strong> {t('attendance:kiosk.stats.active')} ·{' '}
        <strong>{stats.pending}</strong> {t('attendance:kiosk.stats.pending')}
      </Typography>

      {geofences.length === 0 && (
        <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
          {t('attendance:kiosk.noGeofenceWarning')}
        </Alert>
      )}

      {devices.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">{t('attendance:kiosk.empty.title')}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('attendance:kiosk.empty.hint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: 'grey.50', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:kiosk.table.name')}</TableCell>
                <TableCell>{t('attendance:kiosk.table.location')}</TableCell>
                <TableCell>{t('attendance:kiosk.table.locationPolicy')}</TableCell>
                <TableCell>{t('attendance:kiosk.table.status')}</TableCell>
                <TableCell>{t('attendance:kiosk.table.lastSeen')}</TableCell>
                <TableCell align="center">{t('attendance:kiosk.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((d) => (
                <TableRow hover key={d.id}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TabletMacIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{d.geofence_name || (d.geofence_id && geoName.get(d.geofence_id)) || '—'}</TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Switch
                        size="small"
                        checked={d.enforce_device_location !== false}
                        disabled={d.status === 'revoked' || savingDeviceIds.has(d.id)}
                        onChange={(e) => patchDeviceLocation(d, e.target.checked)}
                        inputProps={{
                          'aria-label': t('attendance:kiosk.deviceLocation.toggleAria', { name: d.name }),
                        }}
                      />
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.25 }}>
                          {d.enforce_device_location !== false
                            ? t('attendance:kiosk.deviceLocation.onTitle')
                            : t('attendance:kiosk.deviceLocation.offTitle')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.25 }}>
                          {d.enforce_device_location !== false
                            ? t('attendance:kiosk.deviceLocation.onHelp')
                            : t('attendance:kiosk.deviceLocation.offHelp')}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{statusChip(d.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString(activeLocale()) : t('attendance:kiosk.table.never')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {d.status === 'revoked' ? (
                      <Tooltip title={t('attendance:kiosk.tooltips.delete')}>
                        <IconButton size="small" color="error" onClick={() => setToDelete(d)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={t('attendance:kiosk.tooltips.getCode')}>
                          <IconButton size="small" color="primary" onClick={() => handleRegenerate(d)}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('attendance:kiosk.tooltips.revoke')}>
                          <IconButton size="small" color="error" onClick={() => setToRevoke(d)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Settings */}
      {settings && (
        <Paper elevation={0} sx={{ width: '100%', mt: 3, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('attendance:kiosk.settings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('attendance:kiosk.settings.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 3 }}>
            <TextField
              type="number"
              size="small"
              label={t('attendance:kiosk.settings.thresholdLabel')}
              helperText={t('attendance:kiosk.settings.thresholdHelp')}
              value={settings.kiosk_face_match_threshold}
              disabled={savingSettings}
              onChange={(e) => setSettings({ ...settings, kiosk_face_match_threshold: Number(e.target.value) })}
              onBlur={(e) => {
                const v = Math.min(100, Math.max(50, Number(e.target.value) || 95));
                patchSetting({ kiosk_face_match_threshold: v });
              }}
              inputProps={{ min: 50, max: 100 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              sx={{ width: 240 }}
              FormHelperTextProps={{ sx: { mx: 0 } }}
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 1, mr: 0 }}
              control={
                <Switch
                  checked={settings.kiosk_allow_pin_fallback}
                  disabled={savingSettings}
                  onChange={(e) => patchSetting({ kiosk_allow_pin_fallback: e.target.checked })}
                />
              }
              label={
                <Box sx={{ pt: 0.5, maxWidth: 320 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('attendance:kiosk.settings.allowPinLabel')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>{t('attendance:kiosk.settings.allowPinHelp')}</Typography>
                </Box>
              }
            />
          </Stack>
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', maxWidth: 560 }}>
              {t('attendance:kiosk.settings.reindexHint')}
            </Typography>
            <Button
              size="small"
              startIcon={reindexing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              disabled={reindexing}
              onClick={handleReindex}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
            >
              {t('attendance:kiosk.settings.reindexBtn')}
            </Button>
          </Box>
        </Paper>
      )}
      <Dialog open={openRegister} onClose={() => setOpenRegister(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('attendance:kiosk.register.title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              label={t('attendance:kiosk.register.nameLabel')}
              placeholder={t('attendance:kiosk.register.namePlaceholder')}
              value={regName}
              onChange={(e) => setRegName(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>{t('attendance:kiosk.register.locationLabel')}</InputLabel>
              <Select
                value={regGeofence}
                label={t('attendance:kiosk.register.locationLabel')}
                onChange={(e) => setRegGeofence(e.target.value)}
              >
                {geofences.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('attendance:kiosk.register.locationDefaultHint')}</FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setOpenRegister(false)} sx={{ textTransform: 'none' }}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={registering || !regName.trim() || !regGeofence}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            {registering ? <CircularProgress size={20} /> : t('attendance:kiosk.register.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pairing-code dialog (shown once) */}
      <Dialog open={!!pairingCode} onClose={() => setPairingCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('attendance:kiosk.pairing.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('attendance:kiosk.pairing.body')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
            <Typography
              sx={{ fontFamily: 'monospace', fontSize: '2rem', fontWeight: 700, letterSpacing: '0.3rem', userSelect: 'all' }}
            >
              {pairingCode}
            </Typography>
            <Tooltip title={copied ? t('attendance:kiosk.pairing.copied') : t('attendance:kiosk.pairing.copy')}>
              <IconButton onClick={handleCopyCode} color={copied ? 'success' : 'default'}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {pairingExpires && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center' }}>
              {t('attendance:kiosk.pairing.expiresAt', { time: new Date(pairingExpires).toLocaleTimeString(activeLocale()) })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button variant="contained" onClick={() => setPairingCode(null)} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('attendance:kiosk.pairing.done')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!toRevoke}
        tone="danger"
        title={t('attendance:kiosk.revoke.title')}
        description={t('attendance:kiosk.revoke.description')}
        subject={toRevoke && <Box sx={{ fontWeight: 600 }}>{toRevoke.name}</Box>}
        confirmLabel={t('attendance:kiosk.revoke.confirm')}
        confirmLoadingLabel={t('attendance:kiosk.revoke.loading')}
        onConfirm={handleRevoke}
        onClose={() => setToRevoke(null)}
      />

      <ConfirmDialog
        open={!!toDelete}
        tone="danger"
        title={t('attendance:kiosk.delete.title')}
        description={t('attendance:kiosk.delete.description')}
        subject={toDelete && <Box sx={{ fontWeight: 600 }}>{toDelete.name}</Box>}
        confirmLabel={t('attendance:kiosk.delete.confirm')}
        confirmLoadingLabel={t('attendance:kiosk.delete.loading')}
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KioskDevices;
